import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { FiRefreshCw, FiSmartphone, FiMonitor, FiCircle, FiClock, FiSearch } from 'react-icons/fi';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import UserActivityHistoryModal from '../Components/UserActivityHistoryModal';
import UserAvatar from '../Components/UserAvatar';
import './AdminPages.css';
import './UserManagement.css';

const DEFAULT_SESSION_STATUS = {
  web: { is_online: false },
  android: { is_online: false },
  ios: { is_online: false },
};

const SessionBadge = ({ label, icon, isOnline }) => (
  <span className={`user-mgmt__session-badge ${isOnline ? 'is-online' : 'is-offline'}`}>
    {icon}
    <span className="user-mgmt__session-label">{label}:</span>
    <FiCircle className="user-mgmt__dot" />
    {isOnline ? 'Online' : 'Offline'}
  </span>
);

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [onlineCount, setOnlineCount] = useState(0);
  const [activityUser, setActivityUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const token = getAuthToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [usersRes, onlineRes] = await Promise.all([
        axios.get(`${API_URL}/admin/users`, { headers }),
        axios.get(`${API_URL}/admin/users/online`, { headers }),
      ]);
      setUsers(usersRes.data || []);
      setOnlineCount((onlineRes.data || []).length);
    } catch (e) {
      console.error('User management fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    const t = setInterval(fetchUsers, 5000);
    return () => clearInterval(t);
  }, [fetchUsers]);

  const displayed = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const rows = (onlineOnly ? users.filter((u) => u.is_online) : users).filter((user) => {
      if (!needle) return true;
      return [user.id, user.user_name, user.email, user.user_phone, user.phone]
        .some((value) => String(value || '').toLowerCase().includes(needle));
    });
    return rows.sort((a, b) => {
      const aAt = a.last_seen_at ? new Date(a.last_seen_at).getTime() : 0;
      const bAt = b.last_seen_at ? new Date(b.last_seen_at).getTime() : 0;
      if (bAt !== aAt) return bAt - aAt;
      return (b.id || 0) - (a.id || 0);
    });
  }, [users, onlineOnly, query]);

  return (
    <div className="admin-page user-mgmt">
      <div className="page-header user-mgmt__header">
        <div>
          <h1>User Management</h1>
          <p>Live login status per platform (Web, Android, iOS) for all registered users.</p>
        </div>
        <button type="button" className="user-mgmt__refresh" onClick={fetchUsers}>
          <FiRefreshCw /> Refresh
        </button>
      </div>

      <div className="user-mgmt__stats">
        <div className="user-mgmt__stat-card">
          <span className="user-mgmt__stat-num">{users.length}</span>
          <span className="user-mgmt__stat-label">Total Users</span>
        </div>
        <div className="user-mgmt__stat-card user-mgmt__stat-card--online">
          <span className="user-mgmt__stat-num">{onlineCount}</span>
          <span className="user-mgmt__stat-label">Online Now</span>
        </div>
      </div>

      <div className="user-mgmt__toolbar">
        <label className="admin-list-search user-mgmt__search">
          <FiSearch aria-hidden="true" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, phone, or ID"
            aria-label="Search users"
          />
        </label>
        <label className="user-mgmt__filter">
          <input
            type="checkbox"
            checked={onlineOnly}
            onChange={(e) => setOnlineOnly(e.target.checked)}
          />
          Show online users only
        </label>
      </div>

      <div className="admin-card user-mgmt__table-wrap">
        {loading ? (
          <p className="user-mgmt__loading">Loading users…</p>
        ) : (
          <table className="user-mgmt__table">
            <thead>
              <tr>
                <th>Photo</th>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Login Status</th>
                <th>Last Seen</th>
                <th>Account</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.length === 0 ? (
                <tr>
                  <td colSpan={8} className="user-mgmt__empty">{query.trim() ? 'No users match that search.' : 'No users match this filter.'}</td>
                </tr>
              ) : (
                displayed.map((u) => {
                  const sessions = u.session_status || DEFAULT_SESSION_STATUS;
                  const webOnline = Boolean(sessions.web?.is_online);
                  const androidOnline = Boolean(sessions.android?.is_online);
                  const iosOnline = Boolean(sessions.ios?.is_online);
                  return (
                    <tr key={u.id} className={u.is_online ? 'user-mgmt__row--online' : ''}>
                      <td><UserAvatar user={u} size={36} /></td>
                      <td>{u.id}</td>
                      <td>{u.user_name}</td>
                      <td>{u.email}</td>
                      <td>
                        <div className="user-mgmt__sessions">
                          <SessionBadge
                            label="Web"
                            icon={<FiMonitor />}
                            isOnline={webOnline}
                          />
                          <SessionBadge
                            label="Android"
                            icon={<FiSmartphone />}
                            isOnline={androidOnline}
                          />
                          <SessionBadge
                            label="iOS"
                            icon={<FiSmartphone />}
                            isOnline={iosOnline}
                          />
                        </div>
                      </td>
                      <td>
                        {u.last_seen_at
                          ? new Date(u.last_seen_at).toLocaleString()
                          : '—'}
                      </td>
                      <td>
                        <span className={u.is_active !== false ? 'user-mgmt__active' : 'user-mgmt__inactive'}>
                          {u.is_active !== false ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn-icon user-mgmt__history-btn"
                          title="Activity history"
                          aria-label={`Activity history for ${u.user_name}`}
                          onClick={() => setActivityUser(u)}
                        >
                          <FiClock />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {activityUser && (
        <UserActivityHistoryModal
          user={activityUser}
          onClose={() => setActivityUser(null)}
        />
      )}
    </div>
  );
};

export default UserManagement;
